import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Unplug, CreditCard, Users, Package, LogOut, User, Lock, X, Key, Download, ShieldCheck, Link2, Terminal } from 'lucide-react';
import { useState, useEffect } from 'react';

const ChangePasswordModal = ({ isOpen, onClose }: { isOpen: boolean, onClose: () => void }) => {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (newPassword !== confirmPassword) {
      return setError('Passwords do not match');
    }

    try {
      const res = await fetch('/v1/auth/password', {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('eric_token')}`
        },
        body: JSON.stringify({ old_password: oldPassword, new_password: newPassword })
      });
      const data = await res.json();
      
      if (data.success) {
        setSuccess('Password updated successfully!');
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setTimeout(() => { onClose(); setSuccess(''); }, 2000);
      } else {
        setError(data.error || 'Failed to update password');
      }
    } catch (err) {
      setError('Network error');
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="glass-panel" style={{ width: '400px', padding: '24px', position: 'relative' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: '16px', right: '16px', background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}><X size={20} /></button>
        <h2 style={{ fontSize: '20px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Lock size={20} style={{ color: 'var(--accent-cyan)' }} />
          Change Password
        </h2>
        
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>Old Password</label>
            <input type="password" value={oldPassword} onChange={e => setOldPassword(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '6px', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--border)', color: '#fff' }} required />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>New Password</label>
            <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '6px', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--border)', color: '#fff' }} required />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>Confirm New Password</label>
            <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '6px', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--border)', color: '#fff' }} required />
          </div>
          
          {error && <div style={{ color: '#ef4444', fontSize: '14px' }}>{error}</div>}
          {success && <div style={{ color: '#22c55e', fontSize: '14px' }}>{success}</div>}
          
          <button type="submit" style={{ marginTop: '8px', padding: '10px', borderRadius: '6px', background: 'var(--accent-cyan)', color: '#000', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
            Save Changes
          </button>
        </form>
      </div>
    </div>
  );
};

export function Layout() {
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [localAgentToken, setLocalAgentToken] = useState(() => localStorage.getItem('eric_mec_local_token'));

  useEffect(() => {
    const syncToken = () => setLocalAgentToken(localStorage.getItem('eric_mec_local_token'));
    window.addEventListener('eric_mec_token_change', syncToken);
    window.addEventListener('storage', syncToken);
    const interval = setInterval(syncToken, 500);
    return () => {
      window.removeEventListener('eric_mec_token_change', syncToken);
      window.removeEventListener('storage', syncToken);
      clearInterval(interval);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('eric_token');
    localStorage.removeItem('eric_role');
    navigate('/login');
  };

  return (
    <div className="layout-container">
      {/* Sidebar */}
      <aside className="sidebar">
        <div style={{ padding: '20px', borderBottom: '1px solid var(--border)' }}>
          <h1 style={{ fontSize: '18px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <img src="/favicon.png" alt="Eric MEC" style={{ width: '30px', height: '30px', borderRadius: '8px', boxShadow: '0 0 10px rgba(56,189,248,0.4)' }} />
            <span className="gradient-text">Eric MEC</span>
          </h1>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
            Memory Enhanced Context
          </div>
        </div>

        <nav style={{ padding: '16px 8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <NavLink
            to="/dashboard"
            style={({ isActive }) => ({
              display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px',
              borderRadius: 'var(--radius-sm)',
              background: isActive ? 'var(--bg-glass)' : 'transparent',
              color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
              border: isActive ? '1px solid var(--border)' : '1px solid transparent',
              transition: 'var(--transition)'
            })}
          >
            <LayoutDashboard size={18} />
            <span style={{ fontSize: '14px', fontWeight: 500 }}>Dashboard</span>
          </NavLink>

          <NavLink
            to="/integrations"
            style={({ isActive }) => ({
              display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px',
              borderRadius: 'var(--radius-sm)',
              background: isActive ? 'var(--bg-glass)' : 'transparent',
              color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
              border: isActive ? '1px solid var(--border)' : '1px solid transparent',
              transition: 'var(--transition)'
            })}
          >
            <Unplug size={18} />
            <span style={{ fontSize: '14px', fontWeight: 500 }}>Integrations (MCP)</span>
          </NavLink>

          <NavLink
            to="/billing"
            style={({ isActive }) => ({
              display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px',
              borderRadius: 'var(--radius-sm)',
              background: isActive ? 'var(--bg-glass)' : 'transparent',
              color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
              border: isActive ? '1px solid var(--border)' : '1px solid transparent',
              transition: 'var(--transition)'
            })}
          >
            <CreditCard size={18} />
            <span style={{ fontSize: '14px', fontWeight: 500 }}>Billing & Usage</span>
          </NavLink>

          <div style={{ marginTop: '24px', marginBottom: '8px', paddingLeft: '16px', fontSize: '11px', fontWeight: 700, letterSpacing: '1px', color: 'var(--text-muted)', textTransform: 'uppercase' }}>
            Development
          </div>

          <a
            href="https://coder.ericai.online"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px',
              borderRadius: 'var(--radius-sm)',
              background: 'transparent',
              color: 'var(--text-secondary)',
              border: '1px solid transparent',
              transition: 'var(--transition)',
              textDecoration: 'none'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = 'var(--text-primary)';
              e.currentTarget.style.background = 'var(--bg-glass)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = 'var(--text-secondary)';
              e.currentTarget.style.background = 'transparent';
            }}
          >
            <Terminal size={18} />
            <span style={{ fontSize: '14px', fontWeight: 500 }}>Ericoder IDE</span>
          </a>

          <div style={{ marginTop: '24px', marginBottom: '8px', paddingLeft: '16px', fontSize: '11px', fontWeight: 700, letterSpacing: '1px', color: 'var(--text-muted)', textTransform: 'uppercase' }}>
            Administration
          </div>

          <NavLink
            to="/admin/tenants"
            style={({ isActive }) => ({
              display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px',
              borderRadius: 'var(--radius-sm)',
              background: isActive ? 'var(--bg-glass)' : 'transparent',
              color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
              border: isActive ? '1px solid var(--border)' : '1px solid transparent',
              transition: 'var(--transition)'
            })}
          >
            <Users size={18} />
            <span style={{ fontSize: '14px', fontWeight: 500 }}>Users & Tenants</span>
          </NavLink>

          <NavLink
            to="/admin/plans"
            style={({ isActive }) => ({
              display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px',
              borderRadius: 'var(--radius-sm)',
              background: isActive ? 'var(--bg-glass)' : 'transparent',
              color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
              border: isActive ? '1px solid var(--border)' : '1px solid transparent',
              transition: 'var(--transition)'
            })}
          >
            <Package size={18} />
            <span style={{ fontSize: '14px', fontWeight: 500 }}>Pricing Plans</span>
          </NavLink>

          <NavLink
            to="/admin/providers"
            style={({ isActive }) => ({
              display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px',
              borderRadius: 'var(--radius-sm)',
              background: isActive ? 'var(--bg-glass)' : 'transparent',
              color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
              border: isActive ? '1px solid var(--border)' : '1px solid transparent',
              transition: 'var(--transition)'
            })}
          >
            <Key size={18} />
            <span style={{ fontSize: '14px', fontWeight: 500 }}>AI Providers</span>
          </NavLink>
        </nav>
        
        <div style={{ padding: '16px', borderTop: '1px solid var(--border)', marginTop: 'auto' }}>
          <button 
            onClick={handleLogout}
            style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', width: '100%', borderRadius: 'var(--radius-sm)', background: 'transparent', color: '#ef4444', border: '1px solid transparent', transition: 'var(--transition)', cursor: 'pointer' }}
          >
            <LogOut size={18} />
            <span style={{ fontSize: '14px', fontWeight: 500 }}>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        {/* Header */}
        <header className="header" style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
            Current Project: <strong>Eric_context_memory</strong>
          </div>
          <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
            {localAgentToken ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 12px', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.4)', borderRadius: '20px', color: '#4ade80', fontSize: '12px', fontWeight: 600, boxShadow: '0 0 10px rgba(16,185,129,0.2)' }}>
                  <ShieldCheck size={15} /> Local Agent Paired
                </div>
                <a 
                  href={`https://coder.ericai.online/#token=${encodeURIComponent(localAgentToken)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 12px', background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.4)', borderRadius: '20px', color: '#38bdf8', fontSize: '12px', fontWeight: 600, boxShadow: '0 0 10px rgba(56,189,248,0.2)', textDecoration: 'none', transition: 'all 0.3s', cursor: 'pointer' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(56,189,248,0.2)'; e.currentTarget.style.boxShadow = '0 0 15px rgba(56,189,248,0.4)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(56,189,248,0.1)'; e.currentTarget.style.boxShadow = '0 0 10px rgba(56,189,248,0.2)'; }}
                >
                  🚀 Open Ericoder IDE
                </a>
              </div>
            ) : (
              <NavLink to="/dashboard" style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 12px', background: 'rgba(56,189,248,0.1)', border: '1px dashed rgba(56,189,248,0.4)', borderRadius: '20px', color: 'var(--accent-cyan)', fontSize: '12px', fontWeight: 600, textDecoration: 'none' }}>
                <Link2 size={15} /> Connect Local Agent
              </NavLink>
            )}
            <a href="/Eric_MEC_ComputeUse.exe" download style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 14px', background: 'linear-gradient(135deg, rgba(56,189,248,0.1) 0%, rgba(14,165,233,0.05) 100%)', border: '1px solid rgba(56,189,248,0.3)', borderRadius: '8px', color: 'var(--accent-cyan)', textDecoration: 'none', fontSize: '13px', fontWeight: 600, transition: 'all 0.3s', boxShadow: '0 0 10px rgba(56,189,248,0.1)' }} onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 0 20px rgba(56,189,248,0.4)'; e.currentTarget.style.background = 'linear-gradient(135deg, rgba(56,189,248,0.2) 0%, rgba(14,165,233,0.1) 100%)' }} onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 0 10px rgba(56,189,248,0.1)'; e.currentTarget.style.background = 'linear-gradient(135deg, rgba(56,189,248,0.1) 0%, rgba(14,165,233,0.05) 100%)' }}>
              <Download size={16} />
              ⬇ Download Eric MEC ComputeUse v3.0
            </a>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', borderLeft: '1px solid var(--border)', paddingLeft: '20px' }}>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>API Token:</div>
              <div className="glass-panel" style={{ padding: '4px 12px', fontSize: '12px', fontFamily: 'var(--font-mono)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                eric_mec_***abc
                <button style={{ color: 'var(--accent-cyan)' }}>Copy</button>
              </div>
            </div>
            
            <div style={{ position: 'relative' }}>
              <button 
                onClick={() => setProfileOpen(!profileOpen)}
                style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text-primary)' }}
              >
                <User size={18} />
              </button>
              
              {profileOpen && (
                <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: '8px', width: '200px', background: '#1e1e24', border: '1px solid var(--border)', borderRadius: '8px', zIndex: 50, boxShadow: '0 4px 20px rgba(0,0,0,0.5)', overflow: 'hidden' }}>
                  <button 
                    onClick={() => { setPasswordModalOpen(true); setProfileOpen(false); }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)' }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
                    style={{ width: '100%', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px', background: 'transparent', border: 'none', borderBottom: '1px solid var(--border)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '14px', transition: 'background 0.2s' }}
                  >
                    <Lock size={16} />
                    Change Password
                  </button>
                  <button 
                    onClick={handleLogout}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)' }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
                    style={{ width: '100%', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px', background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '14px', transition: 'background 0.2s' }}
                  >
                    <LogOut size={16} />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Modal */}
        <ChangePasswordModal isOpen={passwordModalOpen} onClose={() => setPasswordModalOpen(false)} />

        {/* Page Content */}
        <div className="page-content">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
